import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

export interface WorkspaceTourStep {
  id: string
  target: string
  title: string
  athenaMessage: string
  position: 'top' | 'bottom' | 'left' | 'right' | 'center'
}

export const WORKSPACE_TOUR_STEPS: WorkspaceTourStep[] = [
  {
    id: 'welcome',
    target: '.jua-canvas',
    title: 'Welcome to your workspace!',
    athenaMessage:
      "This is your customizable dashboard. I've set up a couple of widgets to get you started. Let me walk you through what you can do here.",
    position: 'center',
  },
  {
    id: 'widgets',
    target: '.jua-widget:first-child',
    title: 'Interactive widgets',
    athenaMessage:
      'Each widget is draggable and resizable. Grab the handle at the top to move it, or drag the edges to resize. Double-click the handle to expand it.',
    position: 'right',
  },
  {
    id: 'add-widget',
    target: '.jua-add-widget-btn',
    title: 'Add new widgets',
    athenaMessage:
      'Click here to add charts, maps, tables, or text blocks. Mix and match to build the perfect view for your trading workflow.',
    position: 'bottom',
  },
  {
    id: 'inputs',
    target: '.jua-inputs-bar',
    title: 'Global inputs',
    athenaMessage:
      'These selectors control all widgets at once. Change the location, variable, timeframe, or model run — every widget updates instantly.',
    position: 'bottom',
  },
  {
    id: 'refresh',
    target: '.jua-widget-refresh',
    title: 'Refresh data',
    athenaMessage:
      'Hit the refresh button on any widget to pull the latest forecast data. You can also change inputs above and all widgets will update automatically.',
    position: 'left',
  },
  {
    id: 'ai-help',
    target: '.athena-btn',
    title: 'Ask Athena for help',
    athenaMessage:
      "You can ask me to build entire dashboards! Try something like: 'Create a wind analysis workspace for Germany'",
    position: 'bottom',
  },
  {
    id: 'done',
    target: '.jua-canvas',
    title: "You're all set!",
    athenaMessage:
      'Start building your workspace or ask me for help anytime. Happy trading!',
    position: 'center',
  },
]

export function useWorkspaceTour() {
  const [isActive, setIsActive] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const location = useLocation()

  useEffect(() => {
    // Auto-start if URL state has workspaceTour: true
    const state = location.state as any
    if (state?.workspaceTour) {
      // Small delay to ensure DOM is fully rendered before starting
      const timer = setTimeout(() => {
        setIsActive(true)
        setCurrentStep(0)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [location.state])

  const completeTour = useCallback(() => {
    setIsActive(false)
    localStorage.setItem('jua-ws-tour-completed', 'true')
  }, [])

  const skipTour = useCallback(() => {
    setIsActive(false)
    localStorage.setItem('jua-ws-tour-completed', 'true')
  }, [])

  const nextStep = useCallback(() => {
    if (currentStep < WORKSPACE_TOUR_STEPS.length - 1) {
      setCurrentStep((prev) => prev + 1)
    } else {
      completeTour()
    }
  }, [currentStep, completeTour])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1)
    }
  }, [currentStep])

  const startTour = useCallback(() => {
    setIsActive(true)
    setCurrentStep(0)
  }, [])

  return {
    isActive,
    currentStep,
    steps: WORKSPACE_TOUR_STEPS,
    startTour,
    completeTour,
    skipTour,
    nextStep,
    prevStep,
  }
}
